/**
 * FILE-OVERVIEW:
 * - 役割: S3上の出稿ファイル取得・一覧・操作を担当。
 * - 主な連携: components/S3Files/*
 * - 本番運用上の注意: バケット/キー規則変更時はXML出稿側と合わせて更新する。
 */

import { list, downloadData } from '@aws-amplify/storage';
import { S3Client, PutObjectCommand, GetObjectCommand, ListObjectsV2Command } from '@aws-sdk/client-s3';
import type { _Object } from '@aws-sdk/client-s3';
import { toUtf16BEUint8Array } from '../utils/utf16';
import { fetchAuthSession } from '@aws-amplify/auth';

/** S3オブジェクト1件を画面表示向けに整形したもの */
export interface S3FileInfo {
  key: string;
  size?: number;
  lastModified?: Date;
  /** キーの末尾（ファイル名のみ） */
  fileName: string;
}

/** 一覧XMLの PublishingInfo 1件 */
export interface PublishingInfo {
  publishingDate: string;
  publishingMedia: string;
  publishingPage: string;
}

/**
 * 一覧XMLの ThemeInfo × ContentsInfo を1件に平坦化した素材情報。
 * CTS由来の値はトリムせずそのまま保持する（分類の全角スペースなど）
 */
export interface MemoContent {
  // テーマ情報
  themeName: string;
  themeComment: string;
  newsGenre: string;
  priority: string;
  memoId: string;
  firstCreatedDate: string;
  /** テーマ自体の作成日時。並べ替えにはこちらの安定した値を使う */
  themeFirstCreatedDate: string;
  lastModifiedDate: string;
  initialRegistrationDate: string;
  // 素材情報
  memoKind: string;
  version: string;
  childMemoId: string;
  headLine: string;
  editInfo: string;
  registrationDate: string;
  subjectCode1: string;
  linkUrl: string;
  mediaStatus: string;
  latestSendDate: string;
  publishingStatus: string;
  publishingInfoList: PublishingInfo[];
  /** listTodayContents が付与する。カレンダー表示日 YYYY-MM-DD */
  dateKey?: string;
  /** listTodayContents が付与する。読み込み元の一覧XML */
  sourceFile?: S3FileInfo;
}

/** 素材をテーマ単位へ集約したもの */
export interface ThemeGroup {
  themeName: string;
  themeComment: string;
  newsGenre: string;
  priority: string;
  memoId: string;
  firstCreatedDate: string;
  lastModifiedDate: string;
  initialRegistrationDate: string;
  dateKey?: string;
  sourceFile?: S3FileInfo;
  contents: MemoContent[];
}

/** updateExistingMemoXml で更新・追加する素材1件 */
export interface MaterialUpdate {
  childMemoId?: string;
  memoKind?: string;
  headline?: string;
  editInfo?: string;
  linkUrl?: string;
  registrationDate?: string;
}

/** updateExistingMemoXml の更新差分。undefined の項目は書き換えない */
export interface MemoXmlUpdates {
  /** 対象テーマの絞り込みに使う */
  memoId?: string;
  themeName?: string;
  themeComment?: string;
  newsGenre?: string;
  priority?: string;
  registrationDate?: string;
  /** 全素材に反映する分類 */
  category?: string;
  /** 既存素材の更新 */
  materials?: MaterialUpdate[];
  /** 追加する素材 */
  newMaterials?: MaterialUpdate[];
  /** 削除する素材の ChildMemoID */
  deletedChildMemoIds?: string[];
}

// S3バケット設定（環境変数から取得、なければデフォルト値を使用）
// 環境ごとの向き先はここに集約する。CTSへの出稿先を誤ると実際の紙面制作に流れるため、
// 各コンポーネントでバケット名を直書きせず必ずこの定数を参照すること
const BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME || 'schedule-memo-cts2app';
export const OUTPUT_BUCKET_NAME = process.env.REACT_APP_S3_OUTPUT_BUCKET_NAME || 'schedule-memo-app2cts';
export const S3_REGION = process.env.REACT_APP_AWS_REGION || 'ap-northeast-1';
const REGION = S3_REGION;

/**
 * 入力プレフィックス配下のS3オブジェクト一覧を取得し、画面表示向け形式に整形する。
 *
 * @param {string} prefix 取得対象プレフィックス
 * @returns {Promise<Array<{key:string,size:number,lastModified:Date,fileName:string}>>}
 */
export const listFiles = async (prefix = ''): Promise<S3FileInfo[]> => {
  try {
    const result = await list({
      path: prefix,
      options: {
        bucket: {
          bucketName: BUCKET_NAME,
          region: REGION,
        },
        listAll: true,
      },
    });

    if (!result.items || result.items.length === 0) {
      return [];
    }

    // ファイル情報を整形
    const files: S3FileInfo[] = result.items.map(item => ({
      key: item.path,
      size: item.size,
      lastModified: item.lastModified,
      // split('/') は必ず1要素以上返すため pop() は実質 string
      fileName: item.path.split('/').pop() ?? '', // ファイル名のみ
    }));

    return files;
  } catch (error) {
    console.error('ファイル一覧取得エラー:', error);
    throw error;
  }
};

/**
 * S3上の指定ファイルをブラウザ経由でダウンロードする。
 *
 * @param {string} fileKey S3キー
 * @returns {Promise<void>}
 *
 * 副作用:
 * - URL.createObjectURL と一時 a 要素を使用してクライアント保存を実行する。
 */
export const downloadFile = async (fileKey: string): Promise<void> => {
  try {
    const downloadResult = await downloadData({
      path: fileKey,
      options: {
        bucket: {
          bucketName: BUCKET_NAME,
          region: REGION,
        },
      },
    }).result;

    // レスポンスボディをBlobに変換
    const blob = await downloadResult.body.blob();

    // ダウンロードリンクを作成
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileKey.split('/').pop() ?? ''; // ファイル名のみ
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('ファイルダウンロードエラー:', error);
    throw error;
  }
};

/**
 * バイト数を人間可読形式へ変換する。
 *
 * @param {number} bytes バイト数
 * @returns {string} 例: 1.5 MB
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * S3上のXMLファイルを文字列で取得する。
 *
 * @param {string} fileKey S3キー
 * @returns {Promise<string>} XML文字列
 */
export const getXmlContent = async (fileKey: string): Promise<string> => {
  try {
    // 一覧XMLはCTS側が同一キーで上書きし続けるため、ブラウザのHTTPキャッシュに
    // 古い内容が残ると削除・編集結果が画面に反映されない。
    // requestHandler の cache: 'no-store' で毎回ネットワークから取得する。
    const session = await fetchAuthSession();
    const s3Client = new S3Client({
      region: REGION,
      credentials: session.credentials,
      requestHandler: { cache: 'no-store' },
    });

    const result = await s3Client.send(new GetObjectCommand({
      Bucket: BUCKET_NAME,
      Key: fileKey,
    }));

    // UTF-16BE(BOM付き)とUTF-8の両方に対応する
    // Body は型上 optional だが、GetObject 成功時は必ず存在する
    const rawBytes = new Uint8Array(await result.Body!.transformToByteArray());
    if (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF) {
      return new TextDecoder('utf-16be').decode(rawBytes);
    }
    return new TextDecoder('utf-8').decode(rawBytes);
  } catch (error) {
    console.error('XMLファイル取得エラー:', error);
    throw error;
  }
};

/**
 * XMLから ThemeInfo/ContentsInfo を抽出し、画面処理向けの素材配列へ正規化する。
 *
 * @param {string} xmlString 入力XML
 * @returns {Array<Object>} 素材情報配列
 *
 * 本番運用上の注意:
 * - 連携元XMLタグ名変更時は selector 一式の更新が必要。
 */
export const parseXmlContents = (xmlString: string): MemoContent[] => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // パースエラーチェック
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XMLのパースに失敗しました');
    }

    const contents: MemoContent[] = [];

    // ThemeInfo要素を取得
    const themeInfos = xmlDoc.getElementsByTagName('ThemeInfo');

    Array.from(themeInfos).forEach((themeInfo) => {
      const themeName = themeInfo.querySelector('ThemeName')?.textContent || '';
      const themeComment = themeInfo.querySelector('ThemeComment')?.textContent || '';
      const newsGenre = themeInfo.querySelector('NewsGenre')?.textContent || '';
      const priority = themeInfo.querySelector('Priority')?.textContent || '';
      const memoId = themeInfo.querySelector('MemoID')?.textContent || '';
      const lastModifiedDate = themeInfo.querySelector('LastModifiedDate')?.textContent || '';
      const initialRegistrationDate = themeInfo.querySelector('InitialRegistrationDate')?.textContent || '';

      // ContentsInfo要素を取得
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');

      // テーマレベルの日付情報
      const firstCreatedDate = themeInfo.querySelector('FirstCreatedDate')?.textContent || '';

      Array.from(contentsInfos).forEach((contentsInfo) => {
        // ContentsInfo内のFirstCreatedDateを優先、なければThemeInfoのものを使用
        const contentFirstCreatedDate = contentsInfo.querySelector('FirstCreatedDate')?.textContent || firstCreatedDate;

        // 掲載情報を取得
        const publishingInfos = contentsInfo.getElementsByTagName('PublishingInfo');
        const publishingInfoList: PublishingInfo[] = Array.from(publishingInfos).map((pubInfo) => {
          // Year/Month/Day形式を優先、なければPublishingDateを使用
          const pubYear = pubInfo.querySelector('PublishingYear')?.textContent || '';
          const pubMonth = pubInfo.querySelector('PublishingMonth')?.textContent || '';
          const pubDay = pubInfo.querySelector('PublishingDay')?.textContent || '';
          const pageName = pubInfo.querySelector('PageName')?.textContent || '';
          const publishingDate = pubInfo.querySelector('PublishingDate')?.textContent || '';
          const publishingMedia = pubInfo.querySelector('PublishingMedia')?.textContent || '';
          const publishingPage = pubInfo.querySelector('PublishingPage')?.textContent || pageName;

          let date = publishingDate;
          if (pubYear && pubMonth && pubDay) {
            date = `${pubYear}/${pubMonth.padStart(2, '0')}/${pubDay.padStart(2, '0')}`;
          }

          return {
            publishingDate: date,
            publishingMedia,
            publishingPage,
          };
        });

        const content: MemoContent = {
          // テーマ情報
          themeName,
          themeComment,
          newsGenre,
          priority,
          memoId,
          firstCreatedDate: contentFirstCreatedDate,
          // テーマ自体の作成日時。ContentsInfoの並び順はCTS側の再生成で変わるため、
          // テーマ単位の表示・並べ替えにはこちらの安定した値を使う
          themeFirstCreatedDate: firstCreatedDate,
          lastModifiedDate,
          initialRegistrationDate,
          // 素材情報
          memoKind: contentsInfo.querySelector('MemoKind')?.textContent || '',
          version: contentsInfo.querySelector('Version')?.textContent || '',
          childMemoId: contentsInfo.querySelector('ChildMemoID')?.textContent || '',
          headLine: contentsInfo.querySelector('HeadLine')?.textContent || '',
          editInfo: contentsInfo.querySelector('EditInfo')?.textContent || '',
          registrationDate: contentsInfo.querySelector('RegistrationDate')?.textContent || '',
          subjectCode1: contentsInfo.querySelector('SubjectCode1')?.textContent || '',
          linkUrl: contentsInfo.querySelector('LinkURL')?.textContent || '',
          mediaStatus: (contentsInfo.querySelector('MediaStatus')?.textContent || '').replace(/\s/g, ''),
          latestSendDate: contentsInfo.querySelector('LatestSendDate')?.textContent || '',
          publishingStatus: (contentsInfo.querySelector('PublishingStatus')?.textContent || '').replace(/\s/g, ''),
          publishingInfoList,
        };

        contents.push(content);
      });
    });

    return contents;
  } catch (error) {
    console.error('XMLパースエラー:', error);
    throw error;
  }
};

/**
 * reporter_memo ファイルのうち最新日付分を取得し、日付キー単位でグルーピングする。
 * CTSは1日2回、その時点の最新一覧XMLをまるごと再アップロードする（当日日付のファイル名）ため、
 * 通常は「最新日付＝当日」だが、開発環境のバケットはCTSが書き込まない仕様のため
 * 当日ファイルが存在しないことがある。当日固定ではなく「存在する最新日付」を採用することで、
 * 開発環境や更新が遅れた場合でも直近の一覧が表示されるようにする。
 *
 * @returns {Promise<Record<string, Array>>} { YYYY-MM-DD: FileInfo[] }
 */
export const listReporterMemoFiles = async (): Promise<Record<string, S3FileInfo[]>> => {
  try {
    // まずプレフィックスなしで全ファイルを取得してみる
    const allFiles = await listFiles('');

    // reporter_memo/プレフィックス付きでも取得してみる
    const filesWithPrefix = await listFiles('reporter_memo/');

    // どちらかで取得できたファイルを使用
    const files = filesWithPrefix.length > 0 ? filesWithPrefix : allFiles;

    // ファイル名から日付部分を抽出（最初の_と2つ目の_の間のYYYYMMDD）
    // 例: 1DATE_20251024_1(メモのみ).xml → 20251024
    const filesWithDate = files
      .map(file => ({ file, match: file.fileName.match(/_(\d{8})_/) }))
      .filter((entry): entry is { file: S3FileInfo; match: RegExpMatchArray } => entry.match !== null);

    if (filesWithDate.length === 0) {
      return {};
    }

    // 存在するファイルのうち最新日付を採用する（当日ファイルが無い環境でのフォールバック）
    const latestDateStr = filesWithDate.reduce(
      (latest, { match }) => (match[1] > latest ? match[1] : latest),
      filesWithDate[0].match[1]
    );

    const filesByDate: Record<string, S3FileInfo[]> = {};
    filesWithDate.forEach(({ file, match }) => {
      const dateStr = match[1]; // 例: "20251024"

      // 最新日付のファイルのみを対象とする
      if (dateStr !== latestDateStr) {
        return;
      }

      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const dateKey = `${year}-${month}-${day}`; // ISO形式: "2025-10-24"

      // 同じ日付に複数のファイルがある場合は配列で保持
      if (!filesByDate[dateKey]) {
        filesByDate[dateKey] = [];
      }
      filesByDate[dateKey].push(file);
    });

    return filesByDate;
  } catch (error) {
    console.error('reporter_memoファイル取得エラー:', error);
    throw error;
  }
};

/**
 * 最新日付のXML群を読み込み、素材情報を横断収集して返す。
 *
 * @returns {Promise<Array<Object>>} 素材情報配列
 *
 * 補足:
 * - InitialRegistrationDate を優先して dateKey を決定し、なければ作成日時・ファイル日付へフォールバックする。
 */
export const listTodayContents = async (): Promise<MemoContent[]> => {
  try {
    // 最新日付のファイル一覧を取得
    const filesByDate = await listReporterMemoFiles();

    const allContents: MemoContent[] = [];

    // 各ファイルのXMLを取得してパース
    for (const fileDateKey of Object.keys(filesByDate)) {
      const files = filesByDate[fileDateKey];
      for (const file of files) {
        try {
          const xmlContent = await getXmlContent(file.key);
          const contents = parseXmlContents(xmlContent);
          // 各素材情報に日付とファイル情報を追加
          contents.forEach(content => {
            // InitialRegistrationDate（登録予定日）からカレンダー表示用の日付を生成
            // フォールバック: firstCreatedDate → ファイルの日付
            // 形式: "2026/01/10 02:50:00" / "20260110" のどちらも受け付ける
            const dateKeyOf = (value: unknown): string => {
              const digits = String(value || '').replace(/[^\d]/g, '');
              if (digits.length < 8) return '';
              return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
            };
            // デフォルトはファイルの日付
            const dateKey = dateKeyOf(content.initialRegistrationDate)
              || dateKeyOf(content.firstCreatedDate)
              || fileDateKey;
            allContents.push({
              ...content,
              dateKey,
              sourceFile: file,
            });
          });
        } catch (err) {
          console.error(`XMLパースエラー (${file.key}):`, err);
        }
      }
    }

    return allContents;
  } catch (error) {
    console.error('素材情報一覧取得エラー:', error);
    throw error;
  }
};

/**
 * 素材配列をテーマ単位へ集約する。
 *
 * @param {Array<Object>} contents 素材情報配列
 * @returns {Array<Object>} テーマ単位の集約配列
 */
export const groupContentsByTheme = (contents: MemoContent[]): ThemeGroup[] => {
  const themeMap: Record<string, ThemeGroup> = {};
  // 同一テーマが複数の一覧XMLに載ることがあるため、素材はChildMemoIDで重複排除する
  const seenChildIds: Record<string, Set<string>> = {};
  // 日時比較用（"2026/07/03 14:42:43" と "20260703" の混在に耐える）
  const timeKey = (value: unknown): string => String(value || '').replace(/[^\d]/g, '');

  contents.forEach((content) => {
    const key = content.memoId || content.themeName || 'unknown';
    if (!themeMap[key]) {
      themeMap[key] = {
        themeName: content.themeName,
        themeComment: content.themeComment,
        newsGenre: content.newsGenre,
        priority: content.priority,
        memoId: content.memoId,
        firstCreatedDate: content.themeFirstCreatedDate || content.firstCreatedDate,
        lastModifiedDate: content.lastModifiedDate,
        initialRegistrationDate: content.initialRegistrationDate,
        dateKey: content.dateKey,
        sourceFile: content.sourceFile,
        contents: [],
      };
      seenChildIds[key] = new Set();
    }

    const theme = themeMap[key];
    // テーマの作成日時は最も古い値を採用する。
    // CTSが再出稿を取り込むと取り込み時刻の複製ができ、そのままでは並び順が入れ替わるため
    const candidate = content.themeFirstCreatedDate || content.firstCreatedDate;
    if (candidate && (!theme.firstCreatedDate || timeKey(candidate) < timeKey(theme.firstCreatedDate))) {
      theme.firstCreatedDate = candidate;
    }

    // ChildMemoIDが同じ素材は先に読み込んだ方を残す（IDが無いものは重複排除しない）
    const childId = content.childMemoId;
    if (childId) {
      if (seenChildIds[key]!.has(childId)) return;
      seenChildIds[key]!.add(childId);
    }
    theme.contents.push(content);
  });

  return Object.values(themeMap);
};

/**
 * XML文字列を閲覧用HTMLへ変換する。
 *
 * @param {string} xmlString 入力XML
 * @returns {string} クラス付きHTML文字列
 */
export const convertXmlToHtml = (xmlString: string): string => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // パースエラーチェック
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XMLのパースに失敗しました');
    }

    const root = xmlDoc.documentElement;

    // XMLをHTMLに変換（要素ごとにクラス付与）
    return `<div class="xml-content">${elementToHtml(root)}</div>`;
  } catch (error) {
    console.error('XML→HTML変換エラー:', error);
    throw error;
  }
};

// 日本語ラベルマッピング
const tagLabels: Record<string, string> = {
  XmlInfo: 'XML情報',
  SubmitDate: '実行日時',
  SearchDateType: '検索日付種別',
  SortDateType: 'ソート日付種別',
  SortOrder: 'ソート順',
  SearchStartDate: '出力開始期間',
  SearchEndDate: '出力終了期間',
  OutputThemeCount: '出力テーマ件数',
  ThemeInfoList: 'テーマ情報一覧',
  ThemeInfo: 'テーマ情報',
  ThemeName: 'テーマ名',
  ThemeComment: 'テーマコメント',
  NewsGenre: 'ニュースジャンル',
  Priority: '優先度',
  MemoID: 'メモID',
  InitialRegistrationDate: '初期登録日',
  FirstCreatedDate: '作成日時',
  LastModifiedDate: '最終更新日時',
  TextInfoCnt: 'テキスト情報数',
  PhotoInfoCnt: '写真情報数',
  ContentsInfoList: '素材情報一覧',
  ContentsInfo: '素材情報',
  MemoKind: 'メモ種別',
  Version: 'バージョン',
  ChildMemoID: '子メモID',
  HeadLine: '見出し',
  EditInfo: '編集情報',
  RegistrationDate: '登録日',
  SubjectCode1: 'サブジェクトコード1',
  LinkURL: 'リンクURL',
  MediaStatus: 'メディア状態',
  LatestSendDate: '最終送信日',
  PublishingStatus: '掲載状態',
  PublishingInfoList: '掲載情報一覧',
  PublishingInfo: '掲載情報',
  PublishingDate: '掲載日',
  PublishingMedia: '掲載メディア',
  PublishingPage: '掲載ページ',
};

// 要素カテゴリ分類
const elementCategories: Record<string, string[]> = {
  header: ['SubmitDate', 'SearchDateType', 'SortDateType', 'SortOrder', 'SearchStartDate', 'SearchEndDate', 'OutputThemeCount'],
  theme: ['ThemeName', 'ThemeComment', 'NewsGenre', 'Priority', 'MemoID'],
  date: ['InitialRegistrationDate', 'FirstCreatedDate', 'LastModifiedDate', 'RegistrationDate', 'LatestSendDate', 'PublishingDate'],
  count: ['TextInfoCnt', 'PhotoInfoCnt'],
  contents: ['MemoKind', 'Version', 'ChildMemoID', 'HeadLine', 'EditInfo', 'SubjectCode1', 'LinkURL'],
  status: ['MediaStatus', 'PublishingStatus'],
  list: ['ThemeInfoList', 'ContentsInfoList', 'PublishingInfoList'],
  item: ['ThemeInfo', 'ContentsInfo', 'PublishingInfo'],
};

// カテゴリ取得
const getCategory = (tagName: string): string => {
  for (const [category, tags] of Object.entries(elementCategories)) {
    if (tags.includes(tagName)) return category;
  }
  return 'default';
};

/**
 * XML要素を再帰的にHTMLに変換（要素ごとにクラス付与）
 */
const elementToHtml = (element: Element): string => {
  const tagName = element.nodeName;
  const label = tagLabels[tagName] || tagName;
  const category = getCategory(tagName);
  const children = Array.from(element.childNodes);

  // 子ノードがテキストのみの場合
  if (children.length === 1 && children[0].nodeType === 3) {
    // nodeType 3（テキストノード）の textContent は必ず string
    const text = (children[0].textContent ?? '').trim();
    if (text) {
      return `<div class="xml-element xml-${tagName} xml-category-${category}">
        <span class="xml-label">${label}:</span>
        <span class="xml-value">${text}</span>
      </div>`;
    }
    return '';
  }

  // 子要素がある場合
  if (element.children.length > 0) {
    let html = `<div class="xml-element xml-${tagName} xml-category-${category}">
      <div class="xml-label xml-section-header">${label}</div>
      <div class="xml-children">`;
    Array.from(element.children).forEach(child => {
      html += elementToHtml(child);
    });
    html += '</div></div>';
    return html;
  }

  return '';
};

/**
 * 生成済みメモXMLを出力バケットへアップロードする。
 *
 * @param {string} xmlString XML文字列
 * @param {string} s3Key 出力先キー（例: article/memo/xxxx.xml）
 * @returns {Promise<string>} 保存先S3キー
 *
 * 仕様:
 * - 本関数は UTF-16BE(BOM付き) に変換して保存する。
 */
export const uploadMemoXml = async (xmlString: string, s3Key: string): Promise<string> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  const xmlBytes = toUtf16BEUint8Array(xmlString);

  const command = new PutObjectCommand({
    Bucket: OUTPUT_BUCKET_NAME,
    Key: s3Key,
    Body: xmlBytes,
    ContentType: 'application/xml;charset=UTF-16BE',
  });

  await s3Client.send(command);
  return s3Key;
};

/**
 * 素材の実体ファイル（画像等）を出力バケットへアップロードする。
 *
 * @param {File|Blob} file アップロード対象ファイル
 * @param {string} s3Key 出力先キー（例: photo/file/xxxx.jpg）
 * @returns {Promise<string>} 保存先S3キー
 */
export const uploadMaterialFile = async (file: File | Blob, s3Key: string): Promise<string> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  const fileBytes = new Uint8Array(await file.arrayBuffer());

  const command = new PutObjectCommand({
    Bucket: OUTPUT_BUCKET_NAME,
    Key: s3Key,
    Body: fileBytes,
    ContentType: file.type || 'application/octet-stream',
  });

  await s3Client.send(command);
  return s3Key;
};

/**
 * 出力バケットの photo/file/ 配下の実体NewsMLを読み、ChildMemoID→添付ファイルキーの対応表を作成する。
 *
 * 背景:
 * - cts2app のテーマXMLはCTS側で再生成されるため、アプリ独自要素での紐づけは保持されない。
 * - 紐づけの正は photo/file/ の実体NewsML（NecProperties.ChildMemoID と ContentItem Href）とする。
 *
 * @param {string} [memoFileId] テーマのファイル名用ID（指定時は該当テーマの実体NewsMLのみ解析）
 * @returns {Promise<Record<string, string>>} { childMemoId: 添付ファイルのS3キー }
 */
export const listMaterialFileMap = async (memoFileId?: string): Promise<Record<string, string>> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  // 1. photo/file/ 配下の実体NewsML（.xml）を列挙
  const entityObjects: _Object[] = [];
  let continuationToken: string | undefined;
  do {
    const result = await s3Client.send(new ListObjectsV2Command({
      Bucket: OUTPUT_BUCKET_NAME,
      Prefix: 'photo/file/',
      ContinuationToken: continuationToken,
    }));
    (result.Contents || []).forEach((obj) => {
      const fileName = obj.Key!.split('/').pop();
      if (!fileName || !fileName.toLowerCase().endsWith('.xml')) return;
      // テーマ指定時はファイル名に _memo_<ID>- を含むものだけ対象にする
      if (memoFileId && !fileName.includes(`_memo_${memoFileId}-`)) return;
      entityObjects.push(obj);
    });
    continuationToken = result.IsTruncated ? result.NextContinuationToken : undefined;
  } while (continuationToken);

  // 更新日時の古い順に処理し、同一ChildMemoIDは最新の実体で上書きする
  entityObjects.sort((a, b) => new Date(a.LastModified!).getTime() - new Date(b.LastModified!).getTime());

  // 2. 各実体NewsMLから ChildMemoID と Href を抽出
  const map: Record<string, string> = {};
  for (const obj of entityObjects) {
    try {
      const getResult = await s3Client.send(new GetObjectCommand({
        Bucket: OUTPUT_BUCKET_NAME,
        Key: obj.Key,
      }));
      const rawBytes = new Uint8Array(await getResult.Body!.transformToByteArray());
      const xmlString = (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF)
        ? new TextDecoder('utf-16be').decode(rawBytes)
        : new TextDecoder('utf-8').decode(rawBytes);

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) continue;

      const childMemoId = xmlDoc.querySelector('Property[FormalName="ChildMemoID"]')?.getAttribute('Value') || '';
      const href = xmlDoc.querySelector('ContentItem[Href]')?.getAttribute('Href') || '';
      if (childMemoId && href) {
        map[childMemoId] = `photo/file/${href}`;
      }
    } catch (e) {
      console.warn('実体NewsMLの解析に失敗:', obj.Key, e);
    }
  }

  return map;
};

/**
 * 出力バケットの article/file/ 配下の実体NewsMLを読み、ChildMemoID→記事本文の対応表を作成する。
 *
 * 背景:
 * - cts2app のテーマXMLに記事本文の格納先が無いため、本文の正は
 *   article/file/ の実体NewsML（NecProperties.ChildMemoID と ContentItem/DataContent）とする。
 *
 * @param {string} [memoFileId] テーマのファイル名用ID（指定時は該当テーマの実体NewsMLのみ解析）
 * @returns {Promise<Record<string, string>>} { childMemoId: 記事本文 }
 */
export const listArticleBodyMap = async (memoFileId?: string): Promise<Record<string, string>> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  // 1. article/file/ 配下の実体NewsML（.xml）を列挙
  const entityObjects: _Object[] = [];
  let continuationToken: string | undefined;
  do {
    const result = await s3Client.send(new ListObjectsV2Command({
      Bucket: OUTPUT_BUCKET_NAME,
      Prefix: 'article/file/',
      ContinuationToken: continuationToken,
    }));
    (result.Contents || []).forEach((obj) => {
      const fileName = obj.Key!.split('/').pop();
      if (!fileName || !fileName.toLowerCase().endsWith('.xml')) return;
      // テーマ指定時はファイル名に _memo_<ID>- を含むものだけ対象にする
      if (memoFileId && !fileName.includes(`_memo_${memoFileId}-`)) return;
      entityObjects.push(obj);
    });
    continuationToken = result.IsTruncated ? result.NextContinuationToken : undefined;
  } while (continuationToken);

  // 更新日時の古い順に処理し、同一ChildMemoIDは最新の実体で上書きする
  entityObjects.sort((a, b) => new Date(a.LastModified!).getTime() - new Date(b.LastModified!).getTime());

  // 2. 各実体NewsMLから ChildMemoID と DataContent を抽出
  const map: Record<string, string> = {};
  for (const obj of entityObjects) {
    try {
      const getResult = await s3Client.send(new GetObjectCommand({
        Bucket: OUTPUT_BUCKET_NAME,
        Key: obj.Key,
      }));
      const rawBytes = new Uint8Array(await getResult.Body!.transformToByteArray());
      const xmlString = (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF)
        ? new TextDecoder('utf-16be').decode(rawBytes)
        : new TextDecoder('utf-8').decode(rawBytes);

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) continue;

      const childMemoId = xmlDoc.querySelector('Property[FormalName="ChildMemoID"]')?.getAttribute('Value') || '';
      const body = xmlDoc.querySelector('DataContent')?.textContent || '';
      if (childMemoId) {
        map[childMemoId] = body;
      }
    } catch (e) {
      console.warn('記事実体NewsMLの解析に失敗:', obj.Key, e);
    }
  }

  return map;
};

/**
 * 出力バケット上の素材実体ファイルをブラウザ経由でダウンロードする。
 *
 * @param {string} fileKey S3キー（例: photo/file/xxxx.jpg）
 * @returns {Promise<void>}
 */
export const downloadMaterialFile = async (fileKey: string): Promise<void> => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  const getResult = await s3Client.send(new GetObjectCommand({
    Bucket: OUTPUT_BUCKET_NAME,
    Key: fileKey,
  }));
  const bytes = new Uint8Array(await getResult.Body!.transformToByteArray());
  const blob = new Blob([bytes], { type: getResult.ContentType || 'application/octet-stream' });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileKey.split('/').pop() ?? '';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * 既存メモXMLを部分更新し、同一キーへ上書き保存する。
 *
 * @param {string} fileKey 対象XMLのS3キー
 * @param {Object} updates 更新差分
 * @returns {Promise<string>} 更新後S3キー
 *
 * 本番運用上の注意:
 * - 元ファイルの文字コード（UTF-16BE/UTF-8）を維持して再保存する。
 * - MemoID/ChildMemoID で対象要素を特定するため、ID運用ルールの変更時は要見直し。
 */
// アプリ内部の "YYYY-MM-DD" / "YYYYMMDD" をCTS側の日付書式へ揃える。
// 既存値がある場合はその書式（区切り・時刻の有無）に合わせる。
const toCtsDateFormat = (value?: string, sample = ''): string => {
  const digits = String(value || '').replace(/[^\d]/g, '');
  if (digits.length < 8) return String(value || '');
  const ymd = `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6, 8)}`;
  // 既存値が8桁数字だけならその書式を維持、それ以外は "YYYY/MM/DD hh:mm:ss" を使う
  if (/^\d{8}$/.test(String(sample).trim())) return digits.slice(0, 8);
  const time = String(sample).match(/\d{2}:\d{2}:\d{2}/);
  return `${ymd} ${time ? time[0] : '00:00:00'}`;
};

export const updateExistingMemoXml = async (fileKey: string, updates: MemoXmlUpdates): Promise<string> => {
  // 1. 既存XMLをバイト列でダウンロード（エンコーディング保持のため）
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
    // ブラウザのHTTPキャッシュに古いXMLが残らないよう毎回ネットワークから取得する
    requestHandler: { cache: 'no-store' },
  });

  const getResult = await s3Client.send(new GetObjectCommand({
    Bucket: BUCKET_NAME,
    Key: fileKey,
  }));
  const rawBytes = new Uint8Array(await getResult.Body!.transformToByteArray());

  // 2. エンコーディング検出・デコード
  let xmlString: string;
  let isUtf16BE = false;
  if (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF) {
    isUtf16BE = true;
    xmlString = new TextDecoder('utf-16be').decode(rawBytes);
  } else {
    xmlString = new TextDecoder('utf-8').decode(rawBytes);
  }

  // 3. 元のXML宣言を保持（XMLSerializerが書き換えるため復元用）
  const xmlDeclMatch = xmlString.match(/^<\?xml[^?]*\?>/);
  const xmlDeclaration = xmlDeclMatch ? xmlDeclMatch[0] : '';

  // 4. DOMパース
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('既存XMLのパースに失敗しました');
  }

  // 5. ThemeInfo要素を更新（MemoIDで照合）
  const themeInfos = xmlDoc.getElementsByTagName('ThemeInfo');

  Array.from(themeInfos).forEach((themeInfo) => {
    const memoId = themeInfo.querySelector('MemoID')?.textContent || '';
    if (updates.memoId && memoId !== updates.memoId) {
      return;
    }

    const setField = (selector: string, value?: string): void => {
      if (value === undefined) return;
      const el = themeInfo.querySelector(selector);
      if (!el) return;
      const before = el.textContent;
      // 新しい値が空で元に値がある場合は上書きしない
      if (!value && before) return;
      if (before !== value) {
        el.textContent = value;
      }
    };

    // 日付フィールドはCTS側の書式（YYYY/MM/DD hh:mm:ss）を壊さないよう正規化して扱う。
    // 日付部分が同じなら書き換えない（アプリの "YYYYMMDD" で上書きするとカレンダー日付が狂う）
    const setDateField = (selector: string, value?: string): void => {
      if (value === undefined) return;
      const el = themeInfo.querySelector(selector);
      if (!el) return;
      const before = el.textContent || '';
      if (!value && before) return;
      const beforeDigits = before.replace(/[^\d]/g, '').substring(0, 8);
      const valueDigits = String(value).replace(/[^\d]/g, '').substring(0, 8);
      if (!valueDigits || beforeDigits === valueDigits) return;
      el.textContent = toCtsDateFormat(value, before);
    };

    setField('ThemeName', updates.themeName);
    setField('ThemeComment', updates.themeComment);
    setField('NewsGenre', updates.newsGenre);
    setField('Priority', updates.priority);
    setDateField('InitialRegistrationDate', updates.registrationDate);

    // 6. ContentsInfo要素を更新（ChildMemoIDで照合）
    if (updates.materials) {
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');
      // forEach のクロージャ内では上の if による絞り込みが効かないため、先に取り出す
      const materials = updates.materials;

      Array.from(contentsInfos).forEach((ci) => {
        const childId = ci.querySelector('ChildMemoID')?.textContent || '';
        const mat = materials.find(m => m.childMemoId === childId);
        if (!mat) return;

        const setCiField = (sel: string, val?: string): void => {
          if (val === undefined) return;
          const el = ci.querySelector(sel);
          if (!el) return;
          const before = el.textContent;
          // 値が渡らない項目は直前の undefined 判定で除外済みのため、
          // ここに来る空文字は「画面で明示的に消した」操作として反映する
          // RegistrationDate: 日付部分が同じなら上書きしない（フォーマット保護）
          if (sel === 'RegistrationDate' && before && val) {
            const beforeDate = before.replace(/[\/ \-:]/g, '').substring(0, 8);
            const valDate = val.replace(/[\/ \-:]/g, '').substring(0, 8);
            if (beforeDate === valDate) return;
          }
          if (before !== val) {
            el.textContent = sel === 'RegistrationDate' ? toCtsDateFormat(val, before ?? '') : val;
          }
        };

        setCiField('MemoKind', mat.memoKind);
        setCiField('HeadLine', mat.headline);
        setCiField('EditInfo', mat.editInfo);
        setCiField('LinkURL', mat.linkUrl);
        setCiField('SubjectCode1', updates.category);
        setCiField('RegistrationDate', mat.registrationDate);
      });
    }

    // 6b. 素材削除: 該当ContentsInfo要素をDOMから除去
    if (updates.deletedChildMemoIds && updates.deletedChildMemoIds.length > 0) {
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');
      // 逆順で削除（インデックスずれ防止）
      const ciArray = Array.from(contentsInfos);
      for (let i = ciArray.length - 1; i >= 0; i--) {
        const childId = ciArray[i].querySelector('ChildMemoID')?.textContent || '';
        if (updates.deletedChildMemoIds.includes(childId)) {
          ciArray[i].parentNode!.removeChild(ciArray[i]);
        }
      }
    }

    // 6c. 素材追加: 新しいContentsInfo要素を追加
    if (updates.newMaterials && updates.newMaterials.length > 0) {
      // ContentsInfoListを探す（なければThemeInfo直下に追加）
      let contentsInfoList = themeInfo.querySelector('ContentsInfoList');
      if (!contentsInfoList) {
        contentsInfoList = xmlDoc.createElement('ContentsInfoList');
        themeInfo.appendChild(contentsInfoList);
      }

      for (const mat of updates.newMaterials) {
        const ci = xmlDoc.createElement('ContentsInfo');
        const fields = [
          ['MemoKind', mat.memoKind || ''],
          ['Version', '1'],
          ['ChildMemoID', mat.childMemoId || ''],
          ['HeadLine', mat.headline || ''],
          ['EditInfo', mat.editInfo || ''],
          ['RegistrationDate', mat.registrationDate ? toCtsDateFormat(mat.registrationDate) : ''],
          ['SubjectCode1', updates.category || ''],
          ['LinkURL', mat.linkUrl || ''],
          ['MediaStatus', ''],
          ['LatestSendDate', ''],
          ['PublishingStatus', '未掲載'],
        ];
        fields.forEach(([tag, val]) => {
          const el = xmlDoc.createElement(tag);
          el.textContent = val;
          ci.appendChild(el);
        });
        contentsInfoList.appendChild(ci);
      }
    }

    // 6d. TextInfoCnt / PhotoInfoCnt を再計算
    const allCi = themeInfo.getElementsByTagName('ContentsInfo');
    let textCnt = 0;
    let photoCnt = 0;
    Array.from(allCi).forEach((ci) => {
      const kind = ci.querySelector('MemoKind')?.textContent || '';
      if (kind === '記事' || kind === '1') textCnt++;
      else photoCnt++;
    });
    const textCntEl = themeInfo.querySelector('TextInfoCnt');
    if (textCntEl) textCntEl.textContent = String(textCnt);
    const photoCntEl = themeInfo.querySelector('PhotoInfoCnt');
    if (photoCntEl) photoCntEl.textContent = String(photoCnt);
  });

  // 7. XMLシリアライズ（元のXML宣言を復元）
  const serializer = new XMLSerializer();
  let updatedXml = serializer.serializeToString(xmlDoc);
  updatedXml = updatedXml.replace(/^<\?xml[^?]*\?>/, '');
  if (xmlDeclaration) {
    updatedXml = xmlDeclaration + updatedXml;
  }

  // 8. 元と同じエンコーディングでアップロード（同一キーで上書き）
  let uploadBytes: Uint8Array;
  if (isUtf16BE) {
    uploadBytes = toUtf16BEUint8Array(updatedXml);
  } else {
    uploadBytes = new TextEncoder().encode(updatedXml);
  }

  const putCommand = new PutObjectCommand({
    Bucket: BUCKET_NAME,
    Key: fileKey,
    Body: uploadBytes,
    ContentType: isUtf16BE ? 'application/xml;charset=UTF-16BE' : 'application/xml;charset=UTF-8',
  });

  await s3Client.send(putCommand);
  return fileKey;
};
